import type {
  BackupPolicyExpression,
  BackupPolicyResourceSelector,
  Condition,
} from '@repo/api-gw-sdk';
import {
  ConditionOperatorEnum,
  LogicalOperator,
  ResourceSelectorMode,
} from '@repo/api-gw-sdk';

import {
  fromTagKeyValues,
  toTagKeyValues,
} from '@/components/queryBuilder/tags/tagKeyValueUtils';
import {
  accountIdExpressionToCondition,
  appsExpressionToCondition,
  cloudProviderExpressionToCondition,
  conditionOperatorByListOperator,
  conditionOperatorByNumericOperator,
  conditionOperatorByScalarOperator,
  conditionToAccountIdExpression,
  conditionToAppsExpression,
  conditionToCloudProviderExpression,
  conditionToDataClassesExpression,
  conditionToEncryptionTypeExpression,
  conditionToEnvironmentExpression,
  conditionToNetworkClassificationExpression,
  conditionToResourceGroupNameExpression,
  conditionToResourceIdExpression,
  conditionToResourceNameExpression,
  conditionToResourceTypeExpression,
  conditionToSensitivityAnnotationsExpression,
  conditionToSourceRegionExpression,
  conditionToSubnetsExpression,
  conditionToTagKeyValuesExpression,
  conditionToTagKeysExpression,
  conditionToVpcExpression,
  dataClassesExpressionToCondition,
  encryptionTypeExpressionToCondition,
  environmentExpressionToCondition,
  networkClassificationExpressionToCondition,
  resourceGroupNameExpressionToCondition,
  resourceIdExpressionToCondition,
  resourceNameExpressionToCondition,
  resourceTypeExpressionToCondition,
  sensitivityAnnotationsExpressionToCondition,
  sourceRegionExpressionToCondition,
  subnetsExpressionToCondition,
  tagKeyValuesExpressionToCondition,
  tagKeysExpressionToCondition,
  toListOperator,
  toNumericOperator,
  toScalarOperator,
  toStringOperator,
  vpcExpressionToCondition,
} from '@/components/resourcesSelection/expressionHelpers';
import {
  createAllExcludedCondition,
  createAllIncludedCondition,
  createCombineCondition,
} from '@/types/advanceFilter';

import type { ResourceSelectionState } from './resourceSelectionState';

export const resourceSelectorExpressionToConditionSelection = (
  selector: BackupPolicyResourceSelector | undefined
): ResourceSelectionState => {
  if (
    selector?.resourceSelectionMode ===
    ResourceSelectorMode.RESOURCE_SELECTOR_MODE_ALL
  ) {
    return {
      condition: createAllIncludedCondition(),
      include: selector.resourceInclusionOverride ?? [],
      exclude: selector.resourceExclusionOverride ?? [],
      resourcesScopeType: 'allIncluded',
    };
  }
  if (
    selector?.resourceSelectionMode ===
    ResourceSelectorMode.RESOURCE_SELECTOR_MODE_NONE
  ) {
    return {
      condition: createAllExcludedCondition(),
      include: selector.resourceInclusionOverride ?? [],
      exclude: selector.resourceExclusionOverride ?? [],
      resourcesScopeType: 'allExcluded',
    };
  }

  return {
    condition: expressionToCondition(selector?.expression),
    include: selector?.resourceInclusionOverride ?? [],
    exclude: selector?.resourceExclusionOverride ?? [],
    resourcesScopeType: 'useConditions',
  };
};

export const conditionSelectionToResourceSelectorExpression = (
  resourceSelection: ResourceSelectionState
): BackupPolicyResourceSelector => ({
  resourceSelectionMode:
    resourceSelection.resourcesScopeType === 'allIncluded'
      ? ResourceSelectorMode.RESOURCE_SELECTOR_MODE_ALL
      : resourceSelection.resourcesScopeType === 'allExcluded'
        ? ResourceSelectorMode.RESOURCE_SELECTOR_MODE_NONE
        : ResourceSelectorMode.RESOURCE_SELECTOR_MODE_CONDITIONAL,
  expression:
    resourceSelection.resourcesScopeType === 'useConditions'
      ? conditionToExpression(resourceSelection.condition)
      : undefined,
  resourceInclusionOverride: resourceSelection.include,
  resourceExclusionOverride: resourceSelection.exclude,
});

const conditionToExpression = (
  condition: Condition | undefined
): BackupPolicyExpression | undefined => {
  if (!condition) {
    return undefined;
  }

  if (condition.conditions?.length) {
    const operands = condition.conditions
      .map(conditionToExpression)
      .filter((expression) => !!expression);

    if (!operands.length) {
      return undefined;
    }
    if (operands.length === 1) {
      return operands[0];
    }

    return {
      group: {
        operator:
          condition.operator === ConditionOperatorEnum.Or
            ? LogicalOperator.OR_OPERATOR
            : LogicalOperator.AND_OPERATOR,
        operands,
      },
    };
  }

  const values = condition.value ?? [];
  const scalarOperator = toScalarOperator(condition.operator);
  const stringOperator = toStringOperator(condition.operator);
  const listOperator = toListOperator(condition.operator);
  const numericOperator = toNumericOperator(condition.operator);

  if (condition.property === 'resourceType' && scalarOperator) {
    return conditionToResourceTypeExpression(values, scalarOperator);
  }
  if (condition.property === 'sensitivityLabels' && scalarOperator) {
    return conditionToSensitivityAnnotationsExpression(values, scalarOperator);
  }
  if (condition.property === 'dataClasses' && listOperator) {
    return conditionToDataClassesExpression(values, listOperator);
  }
  if (condition.property === 'environment' && scalarOperator) {
    return conditionToEnvironmentExpression(values, scalarOperator);
  }
  if (condition.property === 'networkClassification' && scalarOperator) {
    return conditionToNetworkClassificationExpression(values, scalarOperator);
  }
  if (condition.property === 'apps' && listOperator) {
    return conditionToAppsExpression(values, listOperator);
  }
  if (condition.property === 'cloudProvider' && scalarOperator) {
    return conditionToCloudProviderExpression(values, scalarOperator);
  }
  if (condition.property === 'accountId' && scalarOperator) {
    return conditionToAccountIdExpression(values, scalarOperator);
  }
  if (condition.property === 'sourceRegion' && scalarOperator) {
    return conditionToSourceRegionExpression(values, scalarOperator);
  }
  if (condition.property === 'vpc' && scalarOperator) {
    return conditionToVpcExpression(values, scalarOperator);
  }
  if (condition.property === 'subnets' && listOperator) {
    return conditionToSubnetsExpression(values, listOperator);
  }
  if (condition.property === 'resourceGroupName' && scalarOperator) {
    return conditionToResourceGroupNameExpression(values, scalarOperator);
  }
  if (condition.property === 'resourceName' && stringOperator) {
    return conditionToResourceNameExpression(values, stringOperator);
  }
  if (condition.property === 'resourceId' && scalarOperator) {
    return conditionToResourceIdExpression(values, scalarOperator);
  }
  if (condition.property === 'tagKeys' && listOperator) {
    return conditionToTagKeysExpression(values, listOperator);
  }
  if (condition.property === 'tagKeyValues' && listOperator) {
    return conditionToTagKeyValuesExpression(values, listOperator);
  }
  if (condition.property === 'sourceAccountTagKeys' && listOperator) {
    return {
      sourceAccountTagKeys: {
        operator: listOperator,
        sourceAccountTagKeys: values,
      },
    };
  }
  if (condition.property === 'sourceAccountTagKeyValues' && listOperator) {
    return {
      sourceAccountTagKeyValues: {
        operator: listOperator,
        sourceAccountTagKeyValues: toTagKeyValues(values),
      },
    };
  }
  if (condition.property === 'globalClusterIdentifier' && scalarOperator) {
    return {
      globalClusterIdentifier: {
        operator: scalarOperator,
        globalClusterIdentifier: values,
      },
    };
  }
  if (condition.property === 'encryptionType' && listOperator) {
    return conditionToEncryptionTypeExpression(values, listOperator);
  }
  if (condition.property === 'sourceStorageSize' && numericOperator) {
    const sizeBytes = Number(values[0]);

    // A threshold the editor has not finished collecting reaches here as an empty or
    // partial value, and a NaN size would match nothing server-side.
    return Number.isFinite(sizeBytes)
      ? { sourceStorageSize: { operator: numericOperator, sizeBytes } }
      : undefined;
  }

  return undefined;
};

const expressionToCondition = (
  expression: BackupPolicyExpression | null | undefined
): Condition | undefined => {
  if (!expression) {
    return undefined;
  }

  if (expression.group) {
    return createCombineCondition(
      expression.group.operands.map(expressionToCondition).filter((c) => !!c),
      expression.group.operator === LogicalOperator.OR_OPERATOR
        ? ConditionOperatorEnum.Or
        : ConditionOperatorEnum.And
    );
  }
  if (expression.resourceType) {
    return resourceTypeExpressionToCondition(expression.resourceType);
  }
  if (expression.dataClasses) {
    return dataClassesExpressionToCondition(expression.dataClasses);
  }
  if (expression.sensitivityAnnotations) {
    return sensitivityAnnotationsExpressionToCondition(
      expression.sensitivityAnnotations
    );
  }
  if (expression.environment) {
    return environmentExpressionToCondition(expression.environment);
  }
  if (expression.networkClassification) {
    return networkClassificationExpressionToCondition(
      expression.networkClassification
    );
  }
  if (expression.apps) {
    return appsExpressionToCondition(expression.apps);
  }
  if (expression.cloudProvider) {
    return cloudProviderExpressionToCondition(expression.cloudProvider);
  }
  if (expression.accountId) {
    return accountIdExpressionToCondition(expression.accountId);
  }
  if (expression.sourceRegion) {
    return sourceRegionExpressionToCondition(expression.sourceRegion);
  }
  if (expression.vpc) {
    return vpcExpressionToCondition(expression.vpc);
  }
  if (expression.subnets) {
    return subnetsExpressionToCondition(expression.subnets);
  }
  if (expression.resourceGroupName) {
    return resourceGroupNameExpressionToCondition(expression.resourceGroupName);
  }
  if (expression.resourceName) {
    return resourceNameExpressionToCondition(expression.resourceName);
  }
  if (expression.resourceId) {
    return resourceIdExpressionToCondition(expression.resourceId);
  }
  if (expression.tagKeys) {
    return tagKeysExpressionToCondition(expression.tagKeys);
  }
  if (expression.tagKeyValues) {
    return tagKeyValuesExpressionToCondition(expression.tagKeyValues);
  }
  if (expression.sourceAccountTagKeys) {
    return {
      property: 'sourceAccountTagKeys',
      operator:
        conditionOperatorByListOperator[
          expression.sourceAccountTagKeys.operator
        ],
      value: expression.sourceAccountTagKeys.sourceAccountTagKeys,
    };
  }
  if (expression.sourceAccountTagKeyValues) {
    return {
      property: 'sourceAccountTagKeyValues',
      operator:
        conditionOperatorByListOperator[
          expression.sourceAccountTagKeyValues.operator
        ],
      value: fromTagKeyValues(
        expression.sourceAccountTagKeyValues.sourceAccountTagKeyValues
      ),
    };
  }
  if (expression.globalClusterIdentifier) {
    return {
      property: 'globalClusterIdentifier',
      operator:
        conditionOperatorByScalarOperator[
          expression.globalClusterIdentifier.operator
        ],
      value: expression.globalClusterIdentifier.globalClusterIdentifier,
    };
  }
  if (expression.encryptionType) {
    return encryptionTypeExpressionToCondition(expression.encryptionType);
  }
  if (expression.sourceStorageSize) {
    return {
      property: 'sourceStorageSize',
      operator:
        conditionOperatorByNumericOperator[
          expression.sourceStorageSize.operator
        ],
      value: [String(expression.sourceStorageSize.sizeBytes)],
    };
  }

  return undefined;
};
